import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";
import { money, spent } from "@/pages/Awards";
import { toast } from "sonner";

const MS = { not_started: "Not started", in_progress: "In progress", done: "Done" };

function Milestones({ a, setA }) {
  const [f, setF] = useState({ title: "", due_date: "" });
  const add = async (e) => {
    e.preventDefault();
    try { setA((await api.post(`/awards/${a.award_id}/milestones`, f)).data); setF({ title: "", due_date: "" }); } catch (e2) { toast.error(formatErr(e2)); }
  };
  const upd = async (mid, status) => setA((await api.patch(`/awards/${a.award_id}/milestones/${mid}`, { status })).data);
  const del = async (mid) => setA((await api.delete(`/awards/${a.award_id}/milestones/${mid}`)).data);
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="milestones-section">
      <h2 className="font-display text-xl font-bold text-slate-900">Milestones</h2>
      <form onSubmit={add} className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input required placeholder="e.g. Hire program coordinator" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="milestone-title-input" />
        <Input type="date" className="sm:w-44" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="milestone-date-input" />
        <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="milestone-add-button"><Plus className="h-4 w-4" /></Button>
      </form>
      <div className="mt-4 divide-y divide-slate-100">
        {(a.milestones || []).map((m) => (
          <div key={m.milestone_id} className="py-3 flex items-center gap-3" data-testid={`milestone-${m.milestone_id}`}>
            <div className="flex-1 min-w-0"><div className={`text-sm font-medium ${m.status === "done" ? "line-through text-slate-400" : "text-slate-900"}`}>{m.title}</div><div className="text-xs text-slate-500">{m.due_date ? `Due ${m.due_date}` : "No date"} · updated by {m.updated_by}</div></div>
            <Select value={m.status} onValueChange={(v) => upd(m.milestone_id, v)}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid={`milestone-status-${m.milestone_id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(MS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={() => del(m.milestone_id)} className="p-1 text-slate-300 hover:text-red-600" data-testid={`milestone-delete-${m.milestone_id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
}

function Expenses({ a, setA }) {
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), category: "", description: "", amount: "" });
  const add = async (e) => {
    e.preventDefault();
    try { setA((await api.post(`/awards/${a.award_id}/expenses`, { ...f, amount: Number(f.amount) })).data); setF({ ...f, category: "", description: "", amount: "" }); } catch (e2) { toast.error(formatErr(e2)); }
  };
  const del = async (eid) => setA((await api.delete(`/awards/${a.award_id}/expenses/${eid}`)).data);
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="expenses-section">
      <h2 className="font-display text-xl font-bold text-slate-900">Fund utilization</h2>
      <form onSubmit={add} className="mt-4 grid grid-cols-2 gap-2">
        <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="expense-date-input" />
        <Input required placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="expense-category-input" />
        <Input required placeholder="Description" className="col-span-2" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="expense-description-input" />
        <Input required type="number" step="0.01" min="0" placeholder="Amount ($)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="expense-amount-input" />
        <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="expense-add-button"><Plus className="h-4 w-4 mr-1" />Log expense</Button>
      </form>
      <table className="w-full mt-4 text-sm">
        <tbody className="divide-y divide-slate-100">
          {(a.expenses || []).map((x) => (
            <tr key={x.expense_id} data-testid={`expense-${x.expense_id}`}>
              <td className="py-2 text-slate-500 font-mono text-xs">{x.date}</td><td className="py-2 text-slate-700">{x.category}</td>
              <td className="py-2 text-slate-600">{x.description}</td><td className="py-2 text-right font-semibold text-slate-900">{money(x.amount)}</td>
              <td className="py-2 text-right"><button onClick={() => del(x.expense_id)} className="p-1 text-slate-300 hover:text-red-600" data-testid={`expense-delete-${x.expense_id}`}><Trash2 className="h-4 w-4" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function AwardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const load = useCallback(() => api.get(`/awards/${id}`).then((r) => setA(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!a) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const s = spent(a);
  const pct = a.amount_awarded ? Math.min(100, Math.round((s / a.amount_awarded) * 100)) : 0;
  const remove = async () => { await api.delete(`/awards/${id}`); navigate("/dashboard/awards"); };
  return (
    <div>
      <Link to="/dashboard/awards" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-6" data-testid="back-to-awards-link"><ArrowLeft className="h-4 w-4" />Awards</Link>
      <div className="flex justify-between gap-4 items-start">
        <div><div className="eyebrow mb-2">{a.funder || "Grant award"}</div><h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="award-title">{a.title}</h1>
          <p className="text-slate-500 mt-1">{a.start_date}{a.end_date ? ` → ${a.end_date}` : ""}</p></div>
        <Button variant="ghost" onClick={remove} data-testid="award-delete-button"><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="mt-8 bg-slate-900 text-white rounded-lg p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div><div className="font-mono text-xs uppercase tracking-widest text-slate-400">Awarded</div><div className="font-display text-3xl font-extrabold mt-1">{money(a.amount_awarded)}</div></div>
        <div><div className="font-mono text-xs uppercase tracking-widest text-slate-400">Spent</div><div className="font-display text-3xl font-extrabold mt-1 text-emerald-400" data-testid="award-spent-total">{money(s)}</div></div>
        <div><div className="font-mono text-xs uppercase tracking-widest text-slate-400">Remaining</div><div className="font-display text-3xl font-extrabold mt-1">{money(a.amount_awarded - s)}</div></div>
        <div className="sm:col-span-3"><Progress value={pct} className="h-2 bg-slate-700" /><div className="text-xs text-slate-400 mt-1.5">{pct}% of funds utilized · report deadlines are on your <Link to="/dashboard/calendar" className="text-emerald-400 underline">compliance calendar</Link></div></div>
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Milestones a={a} setA={setA} />
        <Expenses a={a} setA={setA} />
      </div>
    </div>
  );
}
